import { computed, Directive, inject, model, output, Signal } from '@angular/core';
import { FiDialogsService } from '../../fi-dialogs';
import { IWidgetBase, IWidgetFilterBase } from '../fi-report-widget-details-base/fi-report-widget-base';
import { guid } from '@fi-shared/utils/guid';

@Directive()
export abstract class NewWidgetFilterSettingsDialogBaseDirective<
    TFilter extends IWidgetFilterBase,
    TReportType,
    TWidget extends IWidgetBase<TFilter, TReportType> = IWidgetBase<TFilter, TReportType>,
> {
    public reportWidget = model.required<TWidget>();
    public currentFilter = model<TFilter>({} as TFilter);
    public onBackButtonClick = output<void>();
    public onCloseDialog = output<void>();
    public onSaveWidget = output<TWidget>();

    private fiDialogsService = inject(FiDialogsService);

    protected abstract widgetName: Signal<string>;

    protected isSaveButtonDisabled = computed(() => {
        const reportWidget = this.reportWidget();
        return !reportWidget.Name?.trim() || !this.isFilterApplied(this.getFilterForValidation());
    });

    protected backButtonClick(): void {
        this.canCloseEditting().then((canClose) => {
            if (canClose) {
                this.onBackButtonClick.emit();
            }
        });
    }

    protected closeDialog(): void {
        this.canCloseEditting().then((canClose) => {
            if (canClose) {
                this.onCloseDialog.emit();
            }
        });
    }

    protected saveWidget(): void {
        this.applyCurrentFilterToWidget();
        this.onSaveWidget.emit(this.reportWidget());
    }

    protected setWidgetName(name: string): void {
        this.reportWidget.update((widget) => ({
            ...widget,
            Name: name,
        }));
    }

    protected setWidgetDescription(description: string): void {
        this.reportWidget.update((widget) => ({
            ...widget,
            Description: description,
        }));
    }

    protected setCurrentFilter(currentFilter: TFilter): void {
        this.currentFilter.set({ ...currentFilter });
    }

    protected setReportWidget(reportWidget: TWidget): void {
        this.reportWidget.set({
            ...reportWidget,
            Settings: {
                ...reportWidget.Settings,
                Filters: reportWidget.Settings.Filters?.map((filter) => ({ ...filter })) || [],
            },
        });
    }

    private applyCurrentFilterToWidget(): void {
        const currentFilter = this.currentFilter();
        if (!this.isFilterApplied(currentFilter)) {
            return;
        }

        const filterForSave = {
            ...currentFilter,
            Guid: currentFilter.Guid || guid(),
        } as TFilter;

        this.currentFilter.set(filterForSave);
        this.reportWidget.update((widget) => ({
            ...widget,
            Settings: {
                ...widget.Settings,
                CurrentFilterGuid: filterForSave.Guid,
                Filters: [filterForSave],
            },
        }));
    }

    private getFilterForValidation(): TFilter {
        const currentFilter = this.currentFilter();
        if (this.isFilterApplied(currentFilter)) {
            return currentFilter;
        }

        return this.reportWidget().Settings.Filters?.[0];
    }

    protected isFilterApplied(filter: TFilter): boolean {
        return !!filter && Object.keys(filter).some((key) => key !== 'Guid');
    }

    private canCloseEditting(): Promise<boolean> {
        return new Promise((resolve) => {
            this.fiDialogsService.discardChangesDialog(
                () => resolve(true),
                () => resolve(false),
            );
        });
    }
}
